import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import Toggler from 'components/form/Toggler.component';
import Badge from 'components/generic/Badge.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Space from 'components/generic/Space.component';
import Tooltip from 'components/generic/Tooltip.component';
import TooltipContentReceptionist from 'components/generic/TooltipContentReceptionist.component';
import CONF from 'config/conf.singleton';
import S from 'constants/string.const';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectCurrentSalonNoShowProtection, selectCurrentSalonStripeAccount } from 'selectors/currentSalon.selectors';
import { isEffectiveReceptionist } from 'selectors/user';
import fromAnyToString from 'utils/converters/fromAnyToString';
import pageScrollToTop from 'utils/dom/pageScrollToTop.util';
import logger from 'utils/logger.util';

import NoShowModal from 'settings/page-elements/setup-nsp-deposit/modals/NoShowModal';
import PaymentFeesModal from 'settings/page-elements/setup-nsp-deposit/modals/PaymentFeesModal';
import StripeModal from 'settings/page-elements/setup-nsp-deposit/modals/StripeModal';

import CN from './NoShowProtectionSetup.module.scss';

const HELP_LINK = 'http://help.getslick.com/en/articles/4175763-no-show-protection-for-online-and-in-store-bookings';

const L = logger('NoShowProtectionSetup');

const NoShowProtectionSetup = ({
  SetSalonNoshowOrDeposit: setSalonNoshowOrDeposit,
  errorEnableModal,
  fetchSalonState,
  hasNoShowProtection,
  hasStripeAccount,
  isFetchingRandomKey,
  isReceptionist,
  onNoShowCheckboxClick,
  random_key: randomKey,
}) => {
  const salonId = useCurrentSalonId();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const { val: isStripeRendered, yay: showModalStripe, nay: hideModalStripe } = useTip(false);
  const { val: isFeesRendered, yay: showModalFees, nay: hideModalFees } = useTip(false);
  const { val: isNoShowOpen, yay: snowModalNoShow, nay: hideModalNoshow } = useTip(false);

  const isNoShowRendered = isNoShowOpen && !errorEnableModal;

  const handleNoShow = useCallback(
    (data) => {
      showModalFees();
      hideModalNoshow();
      setSalonNoshowOrDeposit(data, salonId);
      pageScrollToTop();
    },
    [hideModalNoshow, salonId, setSalonNoshowOrDeposit, showModalFees],
  );

  const handleSetup = useCallback(() => {
    snowModalNoShow();
    pageScrollToTop();
  }, [snowModalNoShow]);

  const handleClose = useCallback(() => {
    hideModalNoshow();
    hideModalFees();
  }, [hideModalFees, hideModalNoshow]);

  const handlePaymentFees = useCallback(() => {
    const { random_key: randomKey, email: EMAIL } = this.props;
    const { has_ncob_stripe_account: hasNcobStripeAccount } = this.props;

    hideModalFees();
    showModalStripe();

    if (hasNcobStripeAccount) {
      hideModalStripe();
    } else {
      window.location.replace(
        `${CONF.stripeRedirectUrl}&state=${randomKey}&stripe_user[email]=${EMAIL}&suggested_capabilities[]=transfers`,
      );
    }

    pageScrollToTop();
  }, [hideModalFees, hideModalStripe, showModalStripe]);

  useEffect(() => {
    if (randomKey || isFetchingRandomKey) return;
    L.unignore('useEffect()', 'fetchSalonState:', fetchSalonState(fromAnyToString(salonId)));
  }, [fetchSalonState, isFetchingRandomKey, randomKey, salonId]);

  // if (!hasNoShowProtection) return null;

  return (
    <div
      data-bem="NoShowProtectionSetup"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      {hasStripeAccount ? (
        <div className={CN.content}>
          <div className={CN.title}>
            <div className={CN.flex}>
              {t('onlineBooking.generalSettings.businessStripeAccount.header3')}
              <div className={CN.gap}>
                <Badge variant="success">
                  <span className={CN.emojiSize}>{S.like}</span>
                  <Space>{t('onlineBooking.generalSettings.businessStripeAccount.badge1')}</Space>
                </Badge>
              </div>
            </div>

            {isReceptionist ? (
              <Tooltip variant="top" content={<TooltipContentReceptionist />}>
                <Toggler tag="NoShowProtectionSetup-toggle" selected={hasNoShowProtection} />
              </Tooltip>
            ) : (
              <Toggler
                tag="NoShowProtectionSetup-toggle"
                onClick={onNoShowCheckboxClick}
                selected={hasNoShowProtection}
              />
            )}
          </div>
          <div className={CN.text}>{t('onlineBooking.generalSettings.businessStripeAccount.description4')}</div>
        </div>
      ) : (
        <div className={CN.content}>
          <div className={CN.title}>{t('onlineBooking.generalSettings.businessStripeAccount.header3')}</div>
          <div className={CN.text}>{t('onlineBooking.generalSettings.businessStripeAccount.description5')}</div>

          <div className={CN.buttonsContainer}>
            {isReceptionist ? (
              <Tooltip variant="top" content={<TooltipContentReceptionist />}>
                <Button variant="primary">
                  {t('onlineBooking.generalSettings.businessStripeAccount.confirmButton')}
                </Button>
              </Tooltip>
            ) : (
              <Button onClick={handleSetup} variant="primary">
                {t('onlineBooking.generalSettings.businessStripeAccount.confirmButton')}
              </Button>
            )}

            <ExternalLink link={HELP_LINK} variant="primary">
              {t('onlineBooking.generalSettings.businessStripeAccount.linkLabel')}
            </ExternalLink>
          </div>
        </div>
      )}
      {isNoShowRendered && (
        <NoShowModal
          btnName={t('onlineBooking.generalSettings.businessStripeAccount.okButton')}
          popupTitle={t('onlineBooking.generalSettings.businessStripeAccount.noShowModal.title')}
          onClickButton={handleNoShow}
          closeModal={handleClose}
        />
      )}
      {/* NOTE: this is old design ! 
      TODO: replace with BeforeSetupStripeModal - check DepositSetup.jsx */}
      {isFeesRendered && (
        <PaymentFeesModal
          btnName={t('onlineBooking.generalSettings.businessStripeAccount.okButton')}
          popupTitle={t('onlineBooking.generalSettings.businessStripeAccount.noShowModal.title')}
          onClickButton={handlePaymentFees}
          closeModal={handleClose}
        />
      )}
      {isStripeRendered && (
        <StripeModal>
          <div className="spinner-text">{t('onlineBooking.generalSettings.businessStripeAccount.stripeModalText')}</div>
        </StripeModal>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  hasStripeAccount: selectCurrentSalonStripeAccount(state),
  hasNoShowProtection: selectCurrentSalonNoShowProtection(state),
  isReceptionist: isEffectiveReceptionist(state),
});

const NoShowProtectionSetupHoc = connect(mapStateToProps)(NoShowProtectionSetup);

export default NoShowProtectionSetupHoc;
